import { AI_MODELS } from 'utils/aiModels';

/**
 * Provider grouping for AI model pickers (playground, business-unit config).
 *
 * Source of truth for membership is apps/saas_back/src/types/AIProviderInterface.ts — the provider
 * arrays there decide which provider actually serves a model. We mirror them here because the
 * backend type files can't be imported across the app boundary; the `Otros` bucket surfaces any
 * drift instead of silently mis-grouping.
 *
 * Grouping is by EXACT membership, never string prefix, because prefixes collide:
 *   - `openai/gpt-oss-*` is served by Groq, not OpenAI.
 *   - `mistral.*` (Bedrock-hosted) is Amazon, while `mistral-*-latest` is native Mistral.
 */

export type ProviderLabel =
  | 'OpenAI'
  | 'Anthropic'
  | 'Gemini'
  | 'Groq'
  | 'xAI (Grok)'
  | 'Amazon'
  | 'Mistral'
  | 'DeepSeek'
  | 'Relif Ensemble'
  | 'Relif Fallback'
  | 'Otros';

const OPENAI = new Set([
  'gpt-realtime-2.1',
  'gpt-realtime-mini',
  'gpt-5.6-sol',
  'gpt-5.6-sol-high',
  'gpt-5.6-sol-medium',
  'gpt-5.6-sol-low',
  'gpt-5.6-sol-none',
  'gpt-5.6-terra',
  'gpt-5.6-terra-high',
  'gpt-5.6-terra-medium',
  'gpt-5.6-terra-low',
  'gpt-5.6-terra-none',
  'gpt-5.6-luna',
  'gpt-5.6-luna-high',
  'gpt-5.6-luna-medium',
  'gpt-5.6-luna-low',
  'gpt-5.6-luna-none',
  'gpt-5.5',
  'gpt-5.5-high',
  'gpt-5.5-medium',
  'gpt-5.5-low',
  'gpt-5.5-none',
  'gpt-5.4',
  'gpt-5.4-high',
  'gpt-5.4-medium',
  'gpt-5.4-low',
  'gpt-5.4-mini',
  'gpt-5.4-nano',
  'gpt-5.2',
  'gpt-5.2-high',
  'gpt-5.2-medium',
  'gpt-5.2-low',
  'gpt-5.2-none',
]);

const GROQ = new Set([
  'meta-llama/llama-4-scout-17b-16e-instruct',
  'moonshotai/kimi-k2-instruct',
  'openai/gpt-oss-20b',
  'openai/gpt-oss-120b',
]);

const GEMINI = new Set([
  'gemini-3.1-pro-preview-high',
  'gemini-3.1-pro-preview-low',
  'gemini-3.6-flash',
  'gemini-3-flash-preview',
  'gemini-3.5-flash-lite',
  'gemini-3.1-flash-lite',
]);

const XAI = new Set(['grok-4.3-low', 'grok-4.3-medium', 'grok-4.3-high', 'grok-4.5']);

const AMAZON = new Set([
  'us.amazon.nova-premier-v1:0',
  'amazon.nova-pro-v1:0',
  'amazon.nova-lite-v1:0',
  'amazon.nova-micro-v1:0',
]);

const MISTRAL = new Set(['mistral-medium-latest']);

const ANTHROPIC = new Set([
  'claude-haiku-4-5',
  'claude-haiku-4-5-no-thinking',
  'claude-opus-5',
  'claude-opus-5-no-thinking',
  'claude-sonnet-5',
  'claude-sonnet-5-no-thinking',
  'claude-sonnet-4-6',
  'claude-sonnet-4-6-no-thinking',
  'claude-sonnet-4-5',
  'claude-sonnet-4-5-no-thinking',
]);

const DEEPSEEK = new Set(['deepseek-reasoner', 'deepseek-chat']);
/** Models retired on the backend that may still linger in the frontend list — hidden from pickers. */
const DEPRECATED = new Set(['gpt-realtime-2']);

/** Resolve the provider that actually serves a model. */
export const getProviderForModel = (model: string): ProviderLabel => {
  if (model.startsWith('relif-ensemble-')) {
    return 'Relif Ensemble';
  }
  if (model.startsWith('relif-fallback-')) {
    return 'Relif Fallback';
  }
  if (OPENAI.has(model)) {
    return 'OpenAI';
  }
  if (ANTHROPIC.has(model)) {
    return 'Anthropic';
  }
  if (GEMINI.has(model)) {
    return 'Gemini';
  }
  if (GROQ.has(model)) {
    return 'Groq';
  }
  if (XAI.has(model)) {
    return 'xAI (Grok)';
  }
  if (AMAZON.has(model)) {
    return 'Amazon';
  }
  if (MISTRAL.has(model)) {
    return 'Mistral';
  }
  if (DEEPSEEK.has(model)) {
    return 'DeepSeek';
  }
  return 'Otros';
};

export interface ModelGroup {
  provider: ProviderLabel;
  models: string[];
}

const PROVIDER_ORDER: ProviderLabel[] = [
  'OpenAI',
  'Anthropic',
  'Gemini',
  'Groq',
  'xAI (Grok)',
  'Amazon',
  'Mistral',
  'DeepSeek',
  'Relif Ensemble',
  'Relif Fallback',
  'Otros',
];

/** True for models we never offer in pickers (deprecated, comparison `-vs-`, dummy/test). */
const isHidden = (model: string): boolean =>
  DEPRECATED.has(model) || model.includes('-vs-') || model.startsWith('dummy');

/**
 * Build the ordered, de-duplicated, provider-grouped model list for pickers.
 * Hidden models (deprecated / `-vs-` / dummy) are excluded.
 */
export const getModelGroups = (): ModelGroup[] => {
  const byProvider = new Map<ProviderLabel, string[]>();
  const seen = new Set<string>();

  for (const model of AI_MODELS) {
    if (seen.has(model) || isHidden(model)) {
      continue;
    }
    seen.add(model);
    const provider = getProviderForModel(model);
    const bucket = byProvider.get(provider);
    if (bucket) {
      bucket.push(model);
    } else {
      byProvider.set(provider, [model]);
    }
  }

  return PROVIDER_ORDER.filter((provider) => byProvider.has(provider)).map((provider) => ({
    provider,
    models: byProvider.get(provider) ?? [],
  }));
};
